import React from 'react';
import { useDispatch } from 'react-redux';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useStaffCan, useSelector } from '~Hooks';
import { useHistory } from 'react-router';
import * as actions from '../../actions';
import TallView from './tall';
import { getDefaultOrderStatus } from './__queries__';
import { useStorage } from '~Components/Storage';

import Responsive from './responsive';
import useFilterOrders from '~Scenes/EventedKitchenDisplay/Hooks/useFilterOrders';

type filter = (filter: string) => boolean;

const GET_DEFAULT_ORDER_STATUS = gql`
  query getDefaultOrderStatus($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      name
      company {
        id
        defaultOrderStatus
        brandPrimaryColor
      }
    }
  }
`;

interface Props {
  venueId: string;
  useResponsiveDisplay?: boolean;
}

const CustomerDisplay: React.FunctionComponent<Props> = ({
  venueId,
  useResponsiveDisplay,
}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const staffCan = useStaffCan();
  const mainMenu = useSelector((state) => state.mainMenu);
  const [{ selectedKDSStation }] = useStorage();

  /**
   * Get current station
   */
  const station = useSelector((state) => {
    if (!state.stations || !selectedKDSStation) {
      return undefined;
    }

    return state.stations.find((s) => s.id === selectedKDSStation);
  });

  const toggleMenu = () => {
    if (staffCan.openMainMenu) {
      dispatch(actions.toggleMainMenu(!mainMenu));
    } else {
      // If staff does not have permission to open menu just send them back to venue menu
      history.push('gateway');
    }
  };

  const defaultStatus = useQuery<getDefaultOrderStatus>(GET_DEFAULT_ORDER_STATUS, {
    variables: { venueId: venueId },
    fetchPolicy: 'no-cache',
  });

  const company = defaultStatus.data?.venue?.company;

  const orders = useFilterOrders({ station }).map((order) => ({
    id: order.id,
    status: order.status,
    easyId: order.tableName || order.realId!,
  }));

  const isCooking = (orderStatus: string) =>
    orderStatus === 'ACCEPTED' || orderStatus === 'FINALIZING';

  const isReady = (orderStatus: string) => orderStatus === 'READY';

  const findOrders = (filter: filter) =>
    orders?.filter((order) =>
      orders.filter((o) => o.easyId === order.easyId).find((match) => filter(match.status)),
    );

  const cooking = findOrders(isCooking) || [];
  const ready = findOrders(isReady) || [];

  if (useResponsiveDisplay) {
    return (
      <Responsive
        brandColor={company?.brandPrimaryColor || '#333'}
        cooking={cooking}
        ready={ready}
        toggleMenu={toggleMenu}
      />
    );
  }

  return <TallView loading={false} cooking={cooking} ready={ready} toggleMenu={toggleMenu} />;
};

export default CustomerDisplay;
